// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API module for creating initial true-up.
 * Initial true-up establishes Hardfin as an authoritative subledger for new organizations
 * by creating a one-time true-up journal entry (JE0) to align the user's GL with
 * Hardfin's computed balances.
 */
import * as z from 'zod';
import {
  CreateInitialTrueUpRequest,
  initialTrueUpCloseRunResponseSchema,
} from '@/accounting/api/types';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { serializeCurrencyForAPI } from '@/currency/utilities/serialize';

/**
 * Schema for validating the response from the create initial true-up API endpoint.
 * Includes both the initial true-up close run data and metadata about the request.
 */
const createInitialTrueUpResponseSchema = z
  .object({
    data: initialTrueUpCloseRunResponseSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({
    ...response,
    initialTrueUpCloseRun: data,
  }));

/**
 * Type definition for the create initial true-up API response.
 * Contains the created initial true-up close run data and request metadata.
 */
type CreateInitialTrueUpResponse = z.infer<
  typeof createInitialTrueUpResponseSchema
>;

/**
 * Creates a new initial true-up close run for the specified period.
 * This generates the onboarding journal entry (JE0) that aligns the user's GL
 * with Hardfin's computed balances.
 *
 * @param request - Object containing year, month, GL balances, and adjustment account name
 * @returns Promise resolving to the created initial true-up close run data and request metadata
 * @throws Will throw an error if the organization already has a locked initial true-up,
 *         the API request fails, or response validation fails
 */
export async function createInitialTrueUp(
  request: CreateInitialTrueUpRequest
): Promise<CreateInitialTrueUpResponse> {
  const url = '/v1/accounting/initial-true-ups';
  const response = await api.post(url, {
    ...request,
    balances: {
      glInventory: serializeCurrencyForAPI(request.balances.glInventory),
      glFixedAssets: serializeCurrencyForAPI(request.balances.glFixedAssets),
      glAccumulatedDepr: serializeCurrencyForAPI(
        request.balances.glAccumulatedDepr
      ),
    },
  });

  return parseResponse(createInitialTrueUpResponseSchema, response.data, {
    schemaName: 'CreateInitialTrueUp',
    endpoint: url,
  });
}
